import React, { useEffect, useState } from 'react';
import './App.css';
import './Privacy.css';
import './Support.css';

const CONTACT_EMAIL = 'walkitoffhealth@gmail.com';
const APP_STORE_URL = 'https://apps.apple.com/us/app/walk-it-off-daily-walk-habit/id6818257483';
const MESSAGE_MAX = 1500;

const TOPICS = [
  ['account', 'Account & sign-in'],
  ['steps', 'Steps & syncing'],
  ['challenges', 'Challenges & friends'],
  ['bug', 'Something isn’t working'],
  ['feedback', 'Feedback or idea'],
  ['other', 'Something else'],
];

// Messages are stored in Supabase and the team is alerted in Discord by a database trigger,
// so no webhook URL ships in the site bundle.
async function submitSupportMessage(body) {
  const url = process.env.REACT_APP_SUPABASE_URL?.replace(/\/$/, '');
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!url || !key) throw new Error(`Support is unavailable right now. Email us at ${CONTACT_EMAIL}.`);

  const response = await fetch(`${url}/rest/v1/rpc/submit_web_support_message`, {
    method: 'POST',
    headers: {
      apikey: key,
      Authorization: `Bearer ${key}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) throw new Error(json?.message || `Something went wrong. Email us at ${CONTACT_EMAIL}.`);
  return json;
}

function Support() {
  const currentYear = new Date().getFullYear();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('account');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Support | Walk It Off';
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Honeypot: real visitors never see or fill this field.
    if (website) {
      setStatus('sent');
      return;
    }
    setStatus('sending');
    setError('');
    try {
      await submitSupportMessage({ p_email: email, p_message: message, p_topic: topic, p_name: name || null });
      setStatus('sent');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  };

  return (
    <div className="App">
      <nav className="nav">
        <a href="/" className="logo-container" aria-label="Walk It Off home">
          <img
            src="/walkitofflogo-dark.svg"
            alt="Walk It Off"
            className="logo-horizontal"
            width="350"
            height="130"
          />
        </a>
        <a href={APP_STORE_URL} className="nav-cta privacy-nav-cta" target="_blank" rel="noopener noreferrer">
          Download
        </a>
      </nav>

      <main className="privacy-page">
        <div className="privacy-container">
          <h1 className="privacy-title">Support</h1>

          {status === 'sent' ? (
            <div className="support-success" role="status">
              <h2>Thanks, we’ve got your message.</h2>
              <p>
                A team member will reply to <strong>{email}</strong>, usually within a day.
                Check your spam folder if you don’t see us.
              </p>
              <a href="/" className="privacy-back">← Back to home</a>
            </div>
          ) : (
            <>
              <p className="support-intro">
                Trouble signing in, steps not syncing, or a question about challenges? Send us a
                message and we’ll reply by email. Already in the app? You can also reach us from
                Settings → Help.
              </p>

              <form className="support-form" onSubmit={handleSubmit}>
                <div className="support-row">
                  <label className="support-field">
                    <span>Name <em>(optional)</em></span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={80}
                      autoComplete="name"
                      disabled={status === 'sending'}
                    />
                  </label>
                  <label className="support-field">
                    <span>Email</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={254}
                      autoComplete="email"
                      required
                      disabled={status === 'sending'}
                    />
                  </label>
                </div>

                <label className="support-field">
                  <span>What’s it about?</span>
                  <select value={topic} onChange={(e) => setTopic(e.target.value)} disabled={status === 'sending'}>
                    {TOPICS.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </label>

                <div className="support-field">
                  <label htmlFor="support-message"><span>Message</span></label>
                  <textarea
                    id="support-message"
                    aria-describedby="support-count"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={MESSAGE_MAX}
                    rows={6}
                    placeholder="Tell us what happened, and which phone you use if it’s about the app."
                    required
                    disabled={status === 'sending'}
                  />
                  <small id="support-count" className="support-count">{message.length}/{MESSAGE_MAX}</small>
                </div>

                <label className="support-honeypot" aria-hidden="true">
                  Website
                  <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
                </label>

                {error && <p className="support-error" role="alert">{error}</p>}

                <button type="submit" className="support-submit" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending…' : 'Send message'}
                </button>
                <p className="support-note">
                  We only use your email to reply. <a href="/privacy">Privacy Policy</a> · Prefer email?{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </p>
              </form>
            </>
          )}
        </div>
      </main>

      <footer className="footer">
        <div className="footer-bottom">
          <span>walkitoff.xyz</span>
          <span>© {currentYear} Walk It Off. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}

export default Support;
